import React from "react";
import PropTypes from "prop-types";
import { Search } from "lucide-react";
import {
  importFileFormatOptions,
  importOptionOptions,
} from "./ToolsPage-options.js";

// Helper component for a single radio group
const RadioGroup = ({ title, name, options, selectedValue, onChange }) => (
  <div className="flex-1 bg-gray-100 p-4 rounded-md border border-gray-200 h-full">
    <h3 className="text-lg font-medium mb-2 text-gray-800">{title}:</h3>
    <div className="flex flex-col space-y-2">
      {options.map((option) => (
        <label key={option.value} className="inline-flex items-center">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={selectedValue === option.value}
            onChange={onChange}
            className="form-radio h-4 w-4 text-cyan-700 border-gray-300 focus:ring-blue-500"
          />
          <span className="ml-2 text-gray-700">{option.label}</span>
        </label>
      ))}
    </div>
  </div>
);

RadioGroup.propTypes = {
  title: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedValue: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

// Handler for file input component
const FileInput = ({ onFileChange, fileName, className = "" }) => {
  const fileInputRef = React.useRef(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div className={`inline-block ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={onFileChange}
        className="hidden"
      />
      <div
        className="flex items-center px-4 py-2 bg-cyan-700 text-gray-100 border border-gray-300 rounded-lg cursor-pointer shadow-sm hover:bg-gray-100 hover:text-gray-700 transition-colors duration-300 ease-in-out"
        onClick={handleButtonClick}
      >
        <Search size={20} className="mr-2" />
        <span className="text-base whitespace-nowrap overflow-hidden text-ellipsis">
          {fileName ? fileName : "Please choose file"}
        </span>
      </div>
    </div>
  );
};

FileInput.propTypes = {
  onFileChange: PropTypes.func.isRequired,
  fileName: PropTypes.string,
  className: PropTypes.string,
};

// Import Section Component (Admin only)
const ImportSection = ({
  dynamicHeight,
  importFileFormat,
  setImportFileFormat,
  importOption,
  setImportOption,
  importFile,
  handleFileChange,
  handleImport,
}) => (
  <div className="flex flex-col p-5 h-full lg:min-h-[30rem] border border-gray-100 rounded-lg w-full max-w-xl bg-gradient-to-r from-cyan-700 to-cyan-800 text-white">
    <h2 className="text-2xl font-bold text-gray-100 mb-4">Import Data</h2>

    {/* RadioGroups Container */}
    <div className="flex gap-4 mb-6" style={{ height: `${dynamicHeight}px` }}>
      <RadioGroup
        title="File Format"
        name="importFormat"
        options={importFileFormatOptions}
        selectedValue={importFileFormat}
        onChange={(e) => setImportFileFormat(e.target.value)}
      />

      <RadioGroup
        title="Import Option"
        name="importOption"
        options={importOptionOptions}
        selectedValue={importOption}
        onChange={(e) => setImportOption(e.target.value)}
      />
    </div>

    {/* File Input Section */}
    <div className="mb-6 text-gray-100">
      <label
        htmlFor="importFileInput"
        className="block text-gray-100 text-lg font-bold mb-2"
      >
        Choose File for Import:
      </label>
      <FileInput
        onFileChange={handleFileChange}
        fileName={importFile?.name}
        className="w-full"
      />
    </div>

    <button
      type="button"
      onClick={handleImport}
      className="w-full mt-auto bg-cyan-600 text-white hover:bg-gray-200 hover:text-gray-800 font-semibold py-3 px-6 border border-gray-300 rounded-md shadow-md transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-opacity-75"
    >
      Import Data
    </button>
  </div>
);

ImportSection.propTypes = {
  dynamicHeight: PropTypes.number.isRequired,
  importFileFormat: PropTypes.string.isRequired,
  setImportFileFormat: PropTypes.func.isRequired,
  importOption: PropTypes.string.isRequired,
  setImportOption: PropTypes.func.isRequired,
  importFile: PropTypes.instanceOf(File),
  handleFileChange: PropTypes.func.isRequired,
  handleImport: PropTypes.func.isRequired,
};

export { ImportSection, FileInput };
